// Data + render logic extracted from RTG_Results_Chain_Cross_Function_Map.html.
// Content is fully static (authored, not user-supplied). DOM is built via
// DOMParser + appendChild to avoid innerHTML.

import { API_BASE_URL } from '../../config';

// DATA is fetched from the server on mount — gated to admin + dept_manager.
let DATA = null;

function statsFor(fid) {
  const es = DATA.edges.filter(e => e.tgt === fid);
  return { mapped: es.filter(e => e.status === 'mapped').length, unmapped: es.filter(e => e.status === 'unmapped').length, total: es.length };
}
function outboundStatsFor(fid) {
  const es = DATA.edges.filter(e => e.srcId === fid);
  return { mapped: es.filter(e => e.status === 'mapped').length, unmapped: es.filter(e => e.status === 'unmapped').length, total: es.length };
}
function inboundCountFor(fid) {
  return DATA.edges.filter(e => e.srcId === fid).length;
}
function targetNameFor(fid) {
  const f = DATA.submitted.find(s => s.id === fid);
  return f ? f.name : fid;
}

// Parse static HTML string into a DocumentFragment via DOMParser.
function htmlToFragment(html) {
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${html}</body>`, 'text/html');
  const frag = document.createDocumentFragment();
  Array.from(doc.body.childNodes).forEach(n => frag.appendChild(n));
  return frag;
}

function replaceChildren(el, ...nodes) {
  while (el.firstChild) el.removeChild(el.firstChild);
  nodes.forEach(n => el.appendChild(n));
}

function cardHTML(f, isBlackBox) {
  if (isBlackBox) {
    const inb = inboundCountFor(f.id);
    return `<div class="card blackbox" data-id="${f.id}" data-blackbox="true"><div class="card-title">${f.name}</div><div class="card-sub">Pulled in by ${inb} inputs</div></div>`;
  }
  const inS = statsFor(f.id);
  const outS = outboundStatsFor(f.id);
  const issueCount = (DATA.validation[f.id] || []).length;

  const inLine = inS.total === 0
    ? `<div class="card-row"><span class="card-row-label">Requested from others</span><span class="card-row-stat" style="color:#888;">none</span></div>`
    : `<div class="card-row"><span class="card-row-label">Requested from others</span><span class="card-row-stat"><span class="stat-g">${inS.mapped}✓</span> <span class="stat-r">${inS.unmapped}✗</span></span></div>`;

  const outLine = outS.total === 0
    ? `<div class="card-row"><span class="card-row-label">Requested by others</span><span class="card-row-stat" style="color:#888;">none</span></div>`
    : `<div class="card-row"><span class="card-row-label">Requested by others</span><span class="card-row-stat"><span class="stat-g">${outS.mapped}✓</span> <span class="stat-r">${outS.unmapped}✗</span></span></div>`;

  const issueLine = issueCount > 0
    ? `<div class="card-row"><span class="card-row-label">Chain logic</span><span class="card-row-stat stat-warn">⚠ ${issueCount} issue${issueCount>1?'s':''}</span></div>`
    : '';

  return `<div class="card" data-id="${f.id}" data-blackbox="false">
    <div class="card-title">${f.name}</div>
    <div class="card-sub">Owner: ${f.owner}</div>
    <div class="card-stats-block">${inLine}${outLine}${issueLine}</div>
  </div>`;
}

function renderCascade(fid) {
  const chain = DATA.chains[fid];
  const issues = DATA.validation[fid] || [];
  const issueMap = {};
  for (const i of issues) issueMap[i.id] = i;

  const ocIds = new Set(chain.outcomes.map(o => o.id));
  const opIds = new Set(chain.outputs.map(o => o.id));

  function linkBadges(links, validSet) {
    if (!links || links.length === 0) return '<span class="chain-links broken">→ no link</span>';
    return '<span class="chain-links">→ ' + links.map(l => {
      const broken = !validSet.has(l) && l !== 'IM-1';
      return `<span class="${broken ? 'chain-links broken' : ''}" data-link="${l}">${l}</span>`;
    }).join(', ') + '</span>';
  }

  function itemClass(id) {
    if (!issueMap[id]) return 'chain-item';
    return issueMap[id].kind === 'broken' ? 'chain-item broken' : 'chain-item has-issue';
  }

  let html = `<div class="cascade">`;
  html += `<div class="cascade-col impact-col"><div class="cascade-col-label">Impact</div>`;
  html += `<div class="chain-item impact-item" data-id="${chain.impact.id}" data-level="impact"><span class="chain-id impact">${chain.impact.id}</span><br>${chain.impact.text}</div></div>`;
  html += `<div class="cascade-col"><div class="cascade-col-label">Outcomes (link to Impact)</div>`;
  for (const oc of chain.outcomes) {
    html += `<div class="${itemClass(oc.id)}" data-id="${oc.id}" data-level="oc" data-links="${(oc.links||[]).join(',')}"><span class="chain-id">${oc.id}</span> ${oc.text} ${linkBadges(oc.links, new Set([chain.impact.id]))}</div>`;
  }
  html += `</div>`;
  html += `<div class="cascade-col"><div class="cascade-col-label">Outputs (link to Outcomes)</div>`;
  for (const op of chain.outputs) {
    html += `<div class="${itemClass(op.id)}" data-id="${op.id}" data-level="op" data-links="${(op.links||[]).join(',')}"><span class="chain-id">${op.id}</span> ${op.text} ${linkBadges(op.links, ocIds)}</div>`;
  }
  html += `</div>`;
  html += `<div class="cascade-col"><div class="cascade-col-label">Activities (link to Outputs)</div>`;
  for (const ac of chain.activities) {
    html += `<div class="${itemClass(ac.id)}" data-id="${ac.id}" data-level="ac" data-links="${(ac.links||[]).join(',')}"><span class="chain-id">${ac.id}</span> ${ac.text} ${linkBadges(ac.links, opIds)}</div>`;
  }
  html += `</div></div>`;
  return html;
}

function renderValidation(fid) {
  const issues = DATA.validation[fid] || [];
  if (issues.length === 0) {
    return `<div class="validation-banner clean">✓ Chain logic is clean — Impact ← Outcomes ← Outputs ← Activities ← Inputs all link correctly per the template.</div>`;
  }
  let html = `<div class="validation-banner issues">⚠ ${issues.length} chain logic issue${issues.length>1?'s':''} found:<ul class="validation-list">`;
  for (const i of issues) html += `<li><b>${i.id}</b> [${i.kind}]: ${i.msg}</li>`;
  html += `</ul></div>`;
  return html;
}

const SHELL_HTML = `
  <div class="container">
    <h1>RTG Results Chain — Cross-Function Dependency Map</h1>
    <div class="subtitle">12 submitted chains · 131 declared inputs · click any function to see its full chain, validation, and dependencies.</div>

    <div class="impact">
      <div class="impact-title">Org-wide impact</div>
      <div class="impact-body">Sustainable, profitable growth — RTG as AI champion of MENA, data-driven, with predictable execution</div>
    </div>

    <div class="summary-grid">
      <div class="summary-block">
        <div class="summary-label">Functions / verticals represented</div>
        <div class="summary-row">
          <div class="summary-stat"><div class="stat-num">22</div><div class="stat-label">Total</div></div>
          <div class="summary-stat"><div class="stat-num g">12</div><div class="stat-label">Submitted chains</div></div>
          <div class="summary-stat"><div class="stat-num r">10</div><div class="stat-label">Black boxes</div></div>
        </div>
      </div>
      <div class="summary-block">
        <div class="summary-label">Input dependencies</div>
        <div class="summary-row">
          <div class="summary-stat"><div class="stat-num">131</div><div class="stat-label">Declared inputs</div></div>
          <div class="summary-stat"><div class="stat-num g">67</div><div class="stat-label">Mapped (51%)</div></div>
          <div class="summary-stat"><div class="stat-num r">64</div><div class="stat-label">Unmapped (49%)</div></div>
        </div>
        <div class="summary-breakdown">
          of 64 unmapped:
          <b>21</b> (33%) source exists but has no matching output ·
          <b>40</b> (62%) source is a black box · <b>3</b> (5%) multi-source (all-BUs aggregate)
        </div>
      </div>
    </div>

    <div class="legend">
      <div class="legend-item"><div class="legend-dot" style="background:#EAF3DE;border:1px solid #639922;"></div>Mapped</div>
      <div class="legend-item"><div class="legend-dot" style="background:#FCEBEB;border:1px solid #E24B4A;"></div>Unmapped</div>
      <div class="legend-item"><div class="legend-dot" style="background:#2C2C2A;"></div>Black box (no chain)</div>
      <div class="legend-item"><div class="legend-dot" style="background:#FAEEDA;border:1px solid #BA7517;"></div>Chain validation issue</div>
    </div>

    <div class="tier-label">Submitted functions — click for full chain + validation + dependencies</div>
    <div class="grid" id="rc-submitted"></div>

    <div class="tier-label">Black boxes — referenced as input sources but no chain submitted</div>
    <div class="grid" id="rc-blackbox"></div>

    <div class="detail" id="rc-detail">
      <div class="detail-empty">Click a function above to see its full chain, validation report, and dependencies.</div>
    </div>
  </div>
`;

export async function mountResultsChain(root) {
  // Render a minimal loading state while we fetch the gated data.
  replaceChildren(root, htmlToFragment('<div class="container"><div class="detail-empty">Loading…</div></div>'));

  try {
    const res = await fetch(`${API_BASE_URL}/api/results-chain`, { credentials: 'include' });
    if (res.status === 401 || res.status === 403) {
      replaceChildren(root, htmlToFragment('<div class="container"><div class="detail-empty">Access denied. This page is restricted to admins and department managers.</div></div>'));
      return;
    }
    if (!res.ok) {
      replaceChildren(root, htmlToFragment(`<div class="container"><div class="detail-empty">Failed to load (HTTP ${res.status}).</div></div>`));
      return;
    }
    DATA = await res.json();
  } catch (e) {
    replaceChildren(root, htmlToFragment('<div class="container"><div class="detail-empty">Network error while loading results chain.</div></div>'));
    return;
  }

  replaceChildren(root, htmlToFragment(SHELL_HTML));

  const submittedHTML = DATA.submitted.map(f => cardHTML(f, false)).join('');
  const blackboxHTML = DATA.blackbox.map(f => cardHTML(f, true)).join('');
  replaceChildren(root.querySelector('#rc-submitted'), htmlToFragment(submittedHTML));
  replaceChildren(root.querySelector('#rc-blackbox'), htmlToFragment(blackboxHTML));

  function selectFn(id, isBlackBox) {
    root.querySelectorAll('.card').forEach(c => c.classList.remove('selected'));
    root.querySelector(`.card[data-id="${id}"]`).classList.add('selected');
    const detail = root.querySelector('#rc-detail');
    const fn = isBlackBox ? DATA.blackbox.find(f => f.id === id) : DATA.submitted.find(f => f.id === id);
    let html = '';

    if (!isBlackBox) {
      const stats = statsFor(id);
      html += `<div class="detail-title">${fn.name}</div>`;
      html += `<div class="detail-subtitle">Owner: ${fn.owner} · ${stats.total} inputs (${stats.mapped} mapped, ${stats.unmapped} unmapped)</div>`;

      html += `<div class="section-header">Function chain — Impact ← Outcomes ← Outputs ← Activities</div>`;
      html += renderValidation(id);
      html += renderCascade(id);

      const inbound = DATA.edges.filter(e => e.tgt === id);
      html += `<div class="section-header">Inputs into ${fn.name} (${inbound.length})</div>`;
      if (inbound.length === 0) {
        html += '<div class="detail-empty">No inputs declared in this chain.</div>';
      } else {
        html += inbound.map(e => `<div class="edge ${e.status}"><div class="edge-src">${e.src} →</div><div class="edge-desc"><b>${e.in_id}:</b> ${e.desc}${e.note ? '<br><i style="color:#888;">' + e.note + '</i>' : ''}</div><div class="edge-status ${e.status}">${e.status}</div></div>`).join('');
      }

      const outbound = DATA.edges.filter(e => e.srcId === id);
      if (outbound.length) {
        html += `<div class="section-header sub">${fn.name} is named as source by ${outbound.length} other input${outbound.length>1?'s':''}</div>`;
        html += outbound.map(e => {
          const tgtName = targetNameFor(e.tgt);
          return `<div class="edge ${e.status}"><div class="edge-src">→ ${tgtName}</div><div class="edge-desc">${e.desc}</div><div class="edge-status ${e.status}">${e.status}</div></div>`;
        }).join('');
      }
    } else {
      const outbound = DATA.edges.filter(e => e.srcId === id);
      html += `<div class="detail-title">${fn.name}</div>`;
      html += `<div class="detail-subtitle">Black box — no chain submitted. Pulled in by ${outbound.length} input${outbound.length>1?'s':''}.</div>`;
      html += `<div class="section-header">Inputs declared from ${fn.name} (all unmapped)</div>`;
      html += outbound.map(e => {
        const tgtName = targetNameFor(e.tgt);
        return `<div class="edge unmapped"><div class="edge-src">→ ${tgtName}</div><div class="edge-desc"><b>${e.in_id}:</b> ${e.desc}</div><div class="edge-status unmapped">unmapped</div></div>`;
      }).join('');
    }
    replaceChildren(detail, htmlToFragment(html));

    detail.querySelectorAll('.chain-item').forEach(el => {
      el.addEventListener('mouseenter', () => {
        const links = (el.dataset.links || '').split(',').filter(Boolean);
        detail.querySelectorAll('.chain-item').forEach(o => o.classList.remove('highlight', 'parent-highlight'));
        el.classList.add('highlight');
        links.forEach(l => {
          const target = detail.querySelector(`.chain-item[data-id="${l}"]`);
          if (target) target.classList.add('parent-highlight');
        });
      });
      el.addEventListener('mouseleave', () => {
        detail.querySelectorAll('.chain-item').forEach(o => o.classList.remove('highlight', 'parent-highlight'));
      });
    });

    detail.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  root.querySelectorAll('.card').forEach(c => {
    c.addEventListener('click', () => selectFn(c.dataset.id, c.dataset.blackbox === 'true'));
  });

  // No default selection — the detail panel shows its "click a function" prompt
  // until the user picks one (don't auto-open Marketing).
}
